/**
 * PATCH /tenants/me/settings payload for the workshop settings pages.
 *
 * The payload is assembled field by field, which is exactly how #1795 happened:
 * `WorkshopSettingsForm` learned to edit the VAT-inclusive hourly rate, the
 * additional-work limit, the late-payment interest and the small-supplies
 * surcharge, and none of them was ever added to the payload — so the PATCH
 * returned 200, the success toast fired, and the change never left the browser.
 *
 * `WORKSHOP_SETTINGS_PAYLOAD_KEYS` is the guard against a repeat: it is typed
 * `Record<keyof WorkshopSettings, …>`, so a new editable field fails typecheck
 * until it is mapped to the settings key that carries it (or to `null` for a
 * field persisted somewhere else).
 *
 * Since #2428 the workshop settings are three saves. Each PATCH must list only
 * the keys that page owns; a shallow merge on the server would otherwise let
 * identity save a stale hourly rate over a pricing change made in another tab.
 */

import type { WorkshopSettings } from '@/types';

/** One save action on the split workshop settings pages (#2428). */
export type WorkshopSettingsSaveGroup = 'identity' | 'pricing' | 'hours';

/**
 * Default selectable net payment terms (days) for a workshop that has
 * never configured the list (#1997). Matches the staff-api / provisioning
 * seed so a freshly opened settings page shows the same values invoicing
 * would use.
 */
export const DEFAULT_PAYMENT_TERMS_DAYS_OPTIONS = [7, 10, 14, 30, 60];

/** Drop invalid entries; fall back to the default when nothing usable remains. */
export function normalizePaymentTermsDaysOptions(raw: unknown): number[] {
  if (!Array.isArray(raw)) {
    return [...DEFAULT_PAYMENT_TERMS_DAYS_OPTIONS];
  }
  const days: number[] = [];
  const seen = new Set<number>();
  for (const item of raw) {
    if (typeof item !== 'number' || !Number.isInteger(item) || item < 1 || item > 365) {
      continue;
    }
    if (seen.has(item)) continue;
    seen.add(item);
    days.push(item);
  }
  return days.length > 0 ? days : [...DEFAULT_PAYMENT_TERMS_DAYS_OPTIONS];
}

/**
 * Form field → tenant-settings key. `null` marks a field this PATCH does not
 * carry; it must be persisted by another request.
 */
export const WORKSHOP_SETTINGS_PAYLOAD_KEYS: Record<keyof WorkshopSettings, string | null> = {
  name: null, // a tenants column, sent via PATCH /tenants/me
  address: 'address',
  city: 'city',
  postalCode: 'postalCode',
  country: 'country',
  phone: 'contactPhone',
  email: 'contactEmail',
  businessId: 'businessId',
  vatNumber: 'vatNumber',
  openingHours: 'businessHours',
  // Workshop day boundaries. Document numbering has its own timezone on a
  // different endpoint; this key is the tenant setting the identity page owns
  // (#2428).
  timezone: 'timezone',
  // No editor anywhere; the form only ever echoed 'EUR' back, overwriting a
  // stored value on every save.
  currency: null,
  laborRateCents: 'laborRateCents',
  laborRateGrossCents: 'laborRateGrossCents',
  vatRates: 'vatRates',
  defaultVatRateId: 'defaultVatRateId',
  schedulingLookaheadDays: 'schedulingLookaheadDays',
  defaultSchedulingType: 'defaultSchedulingType',
  defaultQuoteValidityDays: 'defaultQuoteValidityDays',
  workdayUnpaidLunchMinutes: 'workdayUnpaidLunchMinutes',
  smallSuppliesSurcharge: 'smallSuppliesSurcharge',
  defaultAdditionalWorkLimitCents: 'defaultAdditionalWorkLimitCents',
  lateInterestRatePercent: 'lateInterestRatePercent',
  lateInterestRatePercentConsumer: 'lateInterestRatePercentConsumer',
  bankIban: 'bankIban',
  bankBic: 'bankBic',
  defaultPickupBufferMinutes: 'defaultPickupBufferMinutes',
  workDurationStepMinutes: 'workDurationStepMinutes',
  serviceBookLinks: 'serviceBookLinks',
  additionalQuestions: 'additionalQuestions',
};

/**
 * Form fields each save group is allowed to persist. `name` and `currency`
 * stay off every list: the tenant name is PATCH /tenants/me, and currency
 * has no editor.
 */
export const WORKSHOP_SETTINGS_SAVE_GROUPS: Record<
  WorkshopSettingsSaveGroup,
  readonly (keyof WorkshopSettings)[]
> = {
  identity: [
    'address',
    'city',
    'postalCode',
    'country',
    'phone',
    'email',
    'businessId',
    'timezone',
    'additionalQuestions',
    'serviceBookLinks',
  ],
  pricing: [
    'vatNumber',
    'laborRateCents',
    'laborRateGrossCents',
    'vatRates',
    'defaultVatRateId',
    'smallSuppliesSurcharge',
    'defaultAdditionalWorkLimitCents',
    'lateInterestRatePercent',
    'lateInterestRatePercentConsumer',
    'bankIban',
    'bankBic',
    'defaultQuoteValidityDays',
  ],
  hours: [
    'openingHours',
    'schedulingLookaheadDays',
    'defaultSchedulingType',
    'workdayUnpaidLunchMinutes',
    'defaultPickupBufferMinutes',
    'workDurationStepMinutes',
  ],
};

/** Empty optional strings must go out as `undefined` or Zod rejects them. */
const emptyToUndefined = (v: string | undefined): string | undefined => (v === '' ? undefined : v);

export function buildWorkshopSettingsPayload(
  settings: WorkshopSettings,
  paymentTermsDaysOptions?: number[],
  group?: WorkshopSettingsSaveGroup,
): Record<string, unknown> {
  const full: Record<string, unknown> = {
    address: emptyToUndefined(settings.address),
    city: emptyToUndefined(settings.city),
    postalCode: emptyToUndefined(settings.postalCode),
    country: emptyToUndefined(settings.country),
    businessId: emptyToUndefined(settings.businessId),
    vatNumber: emptyToUndefined(settings.vatNumber),
    bankIban: emptyToUndefined(settings.bankIban),
    bankBic: emptyToUndefined(settings.bankBic),
    timezone: settings.timezone,
    // Staff type the VAT-inclusive rate; the API owns the gross → net
    // conversion and never persists a second copy of it (#1600/#1708). A
    // cleared field is a real zero, and a zero gross is ignored server-side,
    // so the net has to carry it.
    laborRateCents: settings.laborRateGrossCents === 0 ? 0 : settings.laborRateCents,
    laborRateGrossCents: settings.laborRateGrossCents,
    defaultAdditionalWorkLimitCents: settings.defaultAdditionalWorkLimitCents,
    lateInterestRatePercent: settings.lateInterestRatePercent,
    lateInterestRatePercentConsumer: settings.lateInterestRatePercentConsumer,
    // Switched off is `undefined` in the form, which JSON would drop and the
    // API would read as "unchanged" (#2644). Send an explicit null instead.
    smallSuppliesSurcharge: settings.smallSuppliesSurcharge ?? null,
    vatRates: settings.vatRates,
    defaultVatRateId: settings.defaultVatRateId,
    contactEmail: emptyToUndefined(settings.email),
    contactPhone: emptyToUndefined(settings.phone),
    schedulingLookaheadDays: settings.schedulingLookaheadDays,
    defaultSchedulingType: settings.defaultSchedulingType,
    defaultQuoteValidityDays: settings.defaultQuoteValidityDays,
    workdayUnpaidLunchMinutes: settings.workdayUnpaidLunchMinutes,
    businessHours: settings.openingHours,
    // These three were edited on the page but never sent (#2618): a cast at
    // the call site kept them out of WorkshopSettings, so the key map above
    // never demanded them and Save reported success on data that never left
    // the browser.
    defaultPickupBufferMinutes: settings.defaultPickupBufferMinutes,
    workDurationStepMinutes: settings.workDurationStepMinutes,
    serviceBookLinks: settings.serviceBookLinks,
    additionalQuestions: settings.additionalQuestions,
    // Edited by PaymentTermsSettings on SettingsPage, not WorkshopSettingsForm
    // (that form's type is WorkshopSettings, which this PATCH key map guards).
    ...(paymentTermsDaysOptions !== undefined ? { paymentTermsDaysOptions } : {}),
  };

  if (group === undefined) {
    return full;
  }

  const allowed = new Set<string>();
  for (const field of WORKSHOP_SETTINGS_SAVE_GROUPS[group]) {
    const key = WORKSHOP_SETTINGS_PAYLOAD_KEYS[field];
    if (key !== null) allowed.add(key);
  }
  if (group === 'pricing' && paymentTermsDaysOptions !== undefined) {
    allowed.add('paymentTermsDaysOptions');
  }
  return Object.fromEntries(Object.entries(full).filter(([key]) => allowed.has(key)));
}
